import React from 'react'
import { useTranslation } from 'react-i18next'
import styled, { withTheme } from 'styled-components'

import Action from '../../components/Action'
import Icon from '../../components/Icon'
import { formatValue } from '../../components/sensors/sensor_value_tile/helpers'

const Popup = styled.div`
  min-width: 220px;
  max-width: min(360px, calc(100vw - 3rem));
  max-height: min(420px, calc(100vh - 5rem));
  padding: 0.5rem;
  padding-top: 1rem;
  overflow-y: auto;
  color: ${({ theme }) => theme.colors.text};
`

const ModuleName = styled.h2`
  margin: 0;
  font-size: 1rem;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
`

const RefreshAction = styled(Action)`
  margin-left: 0.5rem;
  color: ${({ theme }) => theme.colors.text};
  outline: none;

  &:focus {
    outline: none;
  }
`

const Section = styled.section`
  & + & {
    margin-top: 0.75rem;
  }
`

const SectionTitle = styled.h3`
  margin: 0 0 0.25rem;
  font-size: 0.875rem;
`

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`

const ListItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  min-height: 24px;
  font-size: 0.875rem;
`

const Value = styled.span`
  margin-left: auto;
  padding-left: 0.75rem;
  white-space: nowrap;
`

const Empty = styled.span`
  color: ${({ theme }) => theme.colors.gray};
  font-size: 0.875rem;
`

const ValveRow = withTheme(({ valve, theme }) => (
  <ListItem>
    <Icon
      small
      bordered={false}
      rotate={valve.currentIsSuspended ? 90 : undefined}
      name={valve.currentIsSuspended ? 'manual' : 'sync'}
    />
    <Icon small bordered={false} name={`circle-${valve.currentValue || 0}`} />
    {valve.currentPressure != null && (
      <Icon small bordered={false} name="tint" color={theme.colors.water} />
    )}
    <span>{valve.name}</span>
    {valve.hasUnreadError && (
      <Icon
        small
        solid
        name="exclamation-circle"
        border={theme.colors.gray}
        background={theme.colors.background}
        color={theme.colors.alert}
      />
    )}
  </ListItem>
))

const SensorRow = ({ sensorValue, t }) => {
  const value = sensorValue.currentValue || {}
  return (
    <ListItem>
      <span>{sensorValue.name}</span>
      <Value>
        {formatValue(value.value)}{' '}
        {t(`attributes:sensorValue:unitOptions:${sensorValue.unit}`)}
      </Value>
    </ListItem>
  )
}

const ModuleHoverPopup = ({ module, valves, sensorValues, onRefresh }) => {
  const { t } = useTranslation()

  return (
    <Popup
      aria-label={t('map:moduleHover.ariaLabel', {
        name: module.mapDisplayName || module.name,
      })}
    >
      <Header>
        <ModuleName>{module.mapDisplayName || module.name}</ModuleName>
        <RefreshAction
          aria-label={t('map:moduleHover.refresh')}
          title={t('map:moduleHover.refresh')}
          onClick={(e) => {
            e.stopPropagation()
            onRefresh()
          }}
        >
          <Icon name="sync" />
        </RefreshAction>
      </Header>
      <Section>
        <SectionTitle>{t('models:valve_plural')}</SectionTitle>
        {valves.length > 0 ? (
          <List>
            {valves.map((valve) => (
              <ValveRow key={valve.id} valve={valve} />
            ))}
          </List>
        ) : (
          <Empty>{t('map:moduleHover.noValves')}</Empty>
        )}
      </Section>
      <Section>
        <SectionTitle>{t('models:sensor_value_plural')}</SectionTitle>
        {sensorValues.length > 0 ? (
          <List>
            {sensorValues.map((sensorValue) => (
              <SensorRow key={sensorValue.id} sensorValue={sensorValue} t={t} />
            ))}
          </List>
        ) : (
          <Empty>{t('map:moduleHover.noSensorValues')}</Empty>
        )}
      </Section>
    </Popup>
  )
}

export default ModuleHoverPopup
